import { ReactNode } from "react";
import { NavLink } from "react-router";

import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { WarningLabel } from "@shared";

import { ClientDetailResponse } from "@/api";
import { useActiveClientTab } from "@/pages/clients/client/hooks/useClientTabs";
import { ClientTab, clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

interface ClientHeaderProps {
  client: ClientDetailResponse;
  subTabs?: ReactNode;
  tabWarnings?: Partial<Record<ClientTab, boolean>>;
}

export const ClientHeader = ({ client, subTabs, tabWarnings = {} }: ClientHeaderProps) => {
  const { t } = useTranslation();
  const activeTab = useActiveClientTab();

  const tabs = [
    { tab: ClientTab.Overview, label: t("clients.client.tabs.overview") },
    { tab: ClientTab.MasterData, label: t("clients.client.tabs.masterData") },
    { tab: ClientTab.ClientNote, label: t("clients.client.tabs.clientNote") },
    { tab: ClientTab.Notes, label: t("clients.client.tabs.notes") },
    { tab: ClientTab.DebtSettlement, label: t("clients.client.tabs.debtSettlement.label") },
  ];

  return (
    <Box display={"flex"} justifyContent={"space-between"} alignItems={"flex-start"}>
      <Typography variant="h1">{client.fullName}</Typography>
      <Stack>
        <ToggleButtonGroup
          color="primary"
          exclusive
          value={activeTab}
          sx={{
            justifyContent: "flex-end",
            ...(!!subTabs && {
              "& .MuiToggleButton-root.Mui-selected": {
                border: "1px solid var(--mui-palette-fsw-blue-10)",
                borderBottom: "none",
                borderRadius: "4px 4px 0 0",
              },
            }),
          }}
        >
          {tabs.map(({ tab, label }) => (
            <ToggleButton key={tab} value={tab} component={NavLink} to={clientPath.byTab({ id: client.clientId, tab })}>
              <WarningLabel warning={tabWarnings[tab] ? t("clients.client.debtSettlement.hasWarnings") : undefined}>
                {label}
              </WarningLabel>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {subTabs}
      </Stack>
    </Box>
  );
};
